import { useState } from "react";

import { Button } from "~/components/ui/button";
import { toastManager } from "~/components/ui/toast";

import { saveBlob } from "../dashboardsExport";
import { artifactExcelSheets, canExportArtifactExcel } from "./artifactExcel";
import { artifactExportFilename } from "./artifactFormat";
import { artifactResolveRequest, type ArtifactWidgetState } from "./artifactResolve";
import type { ArtifactParameterValues } from "./artifactParameters";
import type { ArtifactSpec, DashboardArtifactVersionSummary } from "./artifactTypes";

export function ArtifactExcelExportButton(props: {
  busy: boolean;
  now: Date;
  spec: ArtifactSpec;
  values: ArtifactParameterValues;
  version: DashboardArtifactVersionSummary;
  widgetState: (widgetId: string) => ArtifactWidgetState;
}) {
  const [preparing, setPreparing] = useState(false);
  const available = !props.busy && canExportArtifactExcel(props.spec, props.widgetState);
  const download = async () => {
    if (preparing || !available) return;
    setPreparing(true);
    try {
      const sheets = artifactExcelSheets({
        spec: props.spec,
        version: props.version.version,
        parameters:
          artifactResolveRequest(props.spec.parameters, props.version.id, props.values, props.now)
            .parameters ?? {},
        exportedAt: new Date(),
        widgetState: props.widgetState,
      });
      const { buildDashboardWorkbook } = await import("../dashboardsWorkbook");
      saveBlob(
        new Blob([buildDashboardWorkbook(sheets)], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        artifactExportFilename(props.spec.title, `v${props.version.version}`, "xlsx"),
      );
    } catch (error) {
      toastManager.add({
        title: "Excel export failed",
        description: error instanceof Error ? error.message : String(error),
        type: "error",
      });
    } finally {
      setPreparing(false);
    }
  };
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      data-dashboard-pdf-export=""
      disabled={preparing || !available}
      title={
        available
          ? "Download the selected dashboard data and charts as an Excel workbook"
          : "Excel export is available when all widgets have finished loading successfully"
      }
      onClick={() => {
        void download();
      }}
    >
      {preparing ? "Preparing Excel…" : "Export Excel"}
    </Button>
  );
}
